import { Capacitor } from '@capacitor/core'
import { useField } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import { isNil } from 'ramda'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { Margin } from '../../layout'
import { Span } from '../../typography/Body'
import { Label } from '../label'

import type { InputHTMLAttributes, MouseEvent } from 'react'

import './Input.css'

import { breakpoints } from '@/config/layout'
import { formatPhoneNumber } from '@/utils/format-phone-number'
import { StFieldContainer, StInputContainer, StInputErrorContainer, StInputIcon } from './Input.styled'

import type { PartiallyRequired } from '@/utils/typescript'

type InputProps = {
  value?: string | number | undefined
  label?: string
  hint?: string
  noMargin?: boolean
  fakeSelect?: boolean
  ignoreTouched?: boolean
  useDefaultLabel?: boolean
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onClear?: () => void
} & PartiallyRequired<InputHTMLAttributes<HTMLInputElement>, 'name'>

const AnimatedIcon = motion(StInputIcon)
const AnimatedFieldContainer = motion(StFieldContainer)
const AnimatedFieldErrorContainer = motion(StInputErrorContainer)

const Input = ({
  label,
  hint,
  noMargin,
  fakeSelect,
  ignoreTouched,
  useDefaultLabel = false,
  onBlur,
  onClear,
  style,
  ...props
}: InputProps) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const [showValue, setShowValue] = useState<boolean>(false)
  const [field, meta, helpers] = useField(props)

  const [fieldValue, setFieldValue] = useState<string | number>(props.value ?? field.value ?? '')

  const isObscured = props.type === 'password'
  const isSearch = props.type === 'search'
  const isDate = props.type === 'date'
  const isTel = props.type === 'tel'
  const isFakeSelect = !!fakeSelect

  const isNative = Capacitor.isNativePlatform()

  // Fake this effect for dev purposes in the browser
  const isSmall = useMediaQuery(breakpoints.toS)
  const isMobile = isNative || isSmall

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    const isPhoneNumber = /(\+\d+)/.test(field.value)
    if (isTel && field.value && isPhoneNumber) {
      const formattedPhoneNumber = formatPhoneNumber(field.value)

      if (formattedPhoneNumber !== field.value) {
        helpers.setValue(formattedPhoneNumber, true)
      }
    }
  }, [])

  useEffect(() => {
    if (props.value !== undefined) {
      setFieldValue(props.value as string)
    } else if (field.value !== undefined) {
      setFieldValue(field.value ?? '')
    }
  }, [fakeSelect, props.readOnly, props.value, field.value])

  useEffect(() => {
    setFieldValue(field.value ?? '')
  }, [field.value])

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleClick(event: MouseEvent<HTMLInputElement>) {
    if (props.onClick) {
      props.onClick(event)
    }

    if (isDate && 'showPicker' in HTMLInputElement.prototype && isMobile) {
      inputRef.current?.showPicker()
    }
  }

  const showDatePicker = () => {
    if (isDate && 'showPicker' in HTMLInputElement.prototype) {
      inputRef.current?.showPicker()
    }
  }

  function toggleShowValue() {
    setShowValue((currentValue) => !currentValue)

    // Keep focus when pressing the button
    inputRef.current?.focus()
  }

  function emptyValue() {
    helpers.setValue('', false)
    setFieldValue('')
    inputRef.current?.focus()
  }

  // Template variables
  // ------------------------------------------------------------------------- /
  const hasError = useMemo(
    () => (meta.touched || ignoreTouched) && !isNil(meta.error),
    [meta.error, ignoreTouched, meta.touched],
  )

  const animatedIconProps = useMemo(
    () => ({
      initial: { opacity: 0, x: 30 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  const animatedErrorMessageProps = useMemo(
    () => ({
      initial: { opacity: 0, y: -30 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -30 },
      transition: { type: 'spring', bounce: 0.1, duration: 0.4 },
    }),
    [],
  )

  return (
    <AnimatedFieldContainer noMargin={noMargin} type={props.type}>
      {label ? (
        <Label
          htmlFor={props.name}
          color={theme.Colors.Neutrals.Darker}
          {...(!useDefaultLabel && {
            type: 'BodyMediumBold' as const,
          })}
        >
          {label} {props.required ? '*' : null}
        </Label>
      ) : null}
      {hint ? (
        <Margin mb={theme.UI.Spacing.Space2}>
          <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Dark}>
            {hint}
          </Span>
        </Margin>
      ) : null}
      <StInputContainer error={hasError}>
        {props.readOnly ? (
          <div className='input'>{fieldValue}</div>
        ) : (
          <input
            {...props}
            {...field}
            ref={inputRef}
            type={showValue ? 'text' : props.type}
            value={fieldValue}
            onClick={handleClick}
            style={{ minHeight: 57, ...style }}
            onInput={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (isDate && event.target.value.length > 10) {
                const date = event.target.value.replace(/\d{5,}/, (match) => match.slice(-4))

                setFieldValue(date)
                helpers.setValue(date, false)
                return
              }

              setFieldValue(event.target.value)
              helpers.setValue(event.target.value, false)
            }}
            onBlur={(event) => {
              helpers.setValue(fieldValue, false)
              helpers.setTouched(true)
              if (onBlur) {
                onBlur(event)
              }
            }}
          />
        )}

        <AnimatePresence mode='popLayout' initial={false}>
          {hasError ? (
            <>
              {isDate ? (
                <AnimatedIcon
                  key='calendar-icon'
                  icon={['fal', 'calendar']}
                  onClick={showDatePicker}
                  {...animatedIconProps}
                  style={{ padding: 0, cursor: 'pointer' }}
                />
              ) : null}
              {isObscured && field.value ? (
                <AnimatedIcon
                  key='obscured-icon'
                  icon={['fal', showValue ? 'eye-slash' : 'eye']}
                  onClick={toggleShowValue}
                  {...animatedIconProps}
                  style={{ paddingRight: 0, cursor: 'pointer' }}
                />
              ) : null}
              <AnimatedIcon key='error-icon' icon={['far', 'triangle-exclamation']} {...animatedIconProps} />
            </>
          ) : isObscured && field.value ? (
            <AnimatedIcon
              key='obscured-icon'
              icon={['fal', showValue ? 'eye-slash' : 'eye']}
              onClick={toggleShowValue}
              {...animatedIconProps}
            />
          ) : isSearch && !field.value ? (
            <AnimatedIcon key='search-icon' icon={['fal', 'magnifying-glass']} {...animatedIconProps} />
          ) : isSearch && field.value ? (
            <AnimatedIcon
              key='close-icon'
              icon={['far', 'close']}
              onClick={() => {
                emptyValue()
                setFieldValue('')
                if (onClear) {
                  onClear()
                }
              }}
              {...animatedIconProps}
            />
          ) : isDate ? (
            <AnimatedIcon
              key='calendar-icon'
              icon={['fal', 'calendar']}
              onClick={showDatePicker}
              style={{ cursor: 'pointer' }}
              {...animatedIconProps}
            />
          ) : isFakeSelect ? (
            <AnimatedIcon key='chevron-icon' icon={['fal', 'chevron-down']} {...animatedIconProps} />
          ) : null}
        </AnimatePresence>
      </StInputContainer>

      <AnimatePresence mode='popLayout'>
        {hasError && meta.error ? (
          <AnimatedFieldErrorContainer key={meta.error} {...animatedErrorMessageProps}>
            <Margin mt={theme.UI.Spacing.Space2} />
            <Span type='BodySmallRegular' color={theme.Colors.Status.Error}>
              {meta.error}
            </Span>
          </AnimatedFieldErrorContainer>
        ) : null}
      </AnimatePresence>
    </AnimatedFieldContainer>
  )
}

export { Input }
